import React from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../AuthContext';
import { Button } from '../ui/button';
import BootstrapStatus from '../BootstrapStatus';
import NotificationCenter from '../NotificationCenter';
// R10e36.80-F1000: PDF job notifications
import NotificationBell from '../NotificationBell';
// R10e36.71.1-F778: Import mobile detection and hidden routes for menu filtering
import { useIsMobile, MOBILE_HIDDEN_ROUTES } from '../DesktopOnly';
// R10e36.68-F823: Import brand config for white-label header
import { BRAND } from '../../brand';
import BrandLogo from '../BrandLogo';
import ImpersonationBanner from '../ImpersonationBanner';  // PC137
import {
  Building2,
  LayoutDashboard,
  FileText,
  DollarSign,
  Upload,
  Users,
  Settings,
  LogOut,
  Shield,
  MapPin,
  PieChart,
  Database,
  FolderSync,
  ClipboardList,
  Building,
  BarChart3,
  FileCheck,
  AlertTriangle,
  // R10e36.60-F450: FileSearch removed — Tax Status sidebar entry retired
  Sparkles,
  TrendingUp,
  Menu,
  X,
  LayoutTemplate,
  Mail,  // R10e36.58-F437
  Landmark,  // R10e36.73-F643: Trust Account icon
  Calendar,  // PC132: Billing Calendar sidebar icon
  GraduationCap,  // PC135: SCHOOL installment plan icon
  Receipt,  // C3: Subscription Billing
  ClipboardCheck  // A20: Collector Intakes
} from 'lucide-react';

// R10e36.59-F449: optional breadcrumb / back-nav slot rendered above page content
const DashboardLayout = ({ children, breadcrumb }) => {
  const navigate = useNavigate();
  const { user, logout } = useAuth();
  const [mobileMenuOpen, setMobileMenuOpen] = React.useState(false);
  // R10e36.71.1-F778: Track mobile viewport for menu filtering
  const isMobile = useIsMobile();

  const handleLogout = () => {
    logout();
    navigate('/login');
  };

  // PC93: Sidebar restructured for SystemAdmin.
  // PC131 (Deploy 7 Batch 3): Two-level nav grouping. Each item now carries a
  // `group` label; the render loop below sorts items into their groups and
  // shows a small header per group. Order of items within a group is
  // preserved. Items with no `group` show under 'Other' (should be empty in
  // steady state — every item should have a group).
  const menuItems = [
    {
      label: 'Dashboard',
      icon: LayoutDashboard,
      path: '/dashboard',
      roles: ['CollectorAdmin', 'CollectorStaff'],  // PC93: was ['SystemAdmin', ...]
    },
    // R10e35b-3-016: Platform Overview for SystemAdmin
    {
      label: 'Platform Overview',
      icon: Shield,
      path: '/admin/platform-overview',
      roles: ['SystemAdmin'],
    },
    // PC93: Consolidated user management (replaces Manage Users + User Approvals + Invite User)
    {
      label: 'User Management',
      icon: Users,
      path: '/admin/users',
      roles: ['SystemAdmin'],
    },
    // R10e36.64.2-F539: Municipal Finance nav link removed — page redirects to /dashboard
    {
      label: 'Parcels',
      icon: MapPin,
      path: '/parcels',
      roles: ['CollectorAdmin', 'CollectorStaff', 'MunicipalViewer'],  // PC93: removed SystemAdmin
    },
    {
      label: 'Payments',
      icon: DollarSign,
      path: '/payments',
      roles: ['CollectorAdmin', 'CollectorStaff'],
    },
    // R10e36.73-F643: Trust Account - PA Act 48 compliance
    // Deploy 7: renamed to "Trust & Reconciliation" per Patrick's audit
    {
      label: 'Trust & Reconciliation',
      icon: Landmark,
      path: '/trust-account',
      roles: ['CollectorAdmin', 'CollectorStaff'],
    },
    // Deploy 7: Payment Review Queue hidden from sidebar. Backend endpoint has no
    // writers — the queue is always empty. Real unmatched-payment workflow lives
    // in Trust & Reconciliation. Feature stays wired at the URL for the day we
    // populate the queue; just doesn't clutter Millie's nav.
    // {
    //   label: 'Payment Review',
    //   icon: AlertTriangle,
    //   path: '/payment-review',
    //   roles: ['CollectorAdmin', 'CollectorStaff'],
    // },
    // R10e36.60-F450: Tax Status entry removed — collectors use Certificate Management instead
    {
      label: 'Collector Operations',
      icon: ClipboardList,
      path: '/operations',
      roles: ['CollectorAdmin', 'CollectorStaff'],
    },
    // R10e35b-8.3-009: Outstanding Bills page
    {
      label: 'Bills',
      icon: FileText,
      path: '/collector/bills',
      roles: ['CollectorAdmin', 'CollectorStaff'],
    },
    // R10e36.73.11-F729: Historical Data Import
    {
      label: 'Historical Import',
      icon: Upload,
      path: '/historical-import',
      roles: ['CollectorAdmin'],  // PC93: removed SystemAdmin (no jurisdiction context without impersonation)
    },
    // R10e35b-8.3-017: Charge Types page (was orphan)
    {
      label: 'Charge Types',
      icon: DollarSign,
      path: '/collector/charges',
      roles: ['CollectorAdmin', 'CollectorStaff'],
    },
    // R10e36.58-F437: Mail Queue for physical mail fulfillment
    // Deploy 7: renamed "Mail Queue" -> "Certificate Mail Queue" to disambiguate
    // from bill-run Mail Export (Collector Operations tab).
    {
      label: 'Certificate Mail Queue',
      icon: Mail,
      path: '/collector/mail-queue',
      roles: ['CollectorAdmin', 'CollectorStaff'],
    },
    // R10e35b-8.3-017: Millage Rates page (was orphan)
    {
      label: 'Millage Rates',
      icon: PieChart,
      path: '/collector/millage-rates',
      roles: ['CollectorAdmin', 'CollectorStaff'],
    },
    // PC132 (Deploy 7 Batch 3 hotfix): Billing Calendar editor
    {
      label: 'Billing Calendar',
      icon: Calendar,
      path: '/collector/billing-calendar',
      roles: ['CollectorAdmin', 'CollectorStaff'],
    },
    // PC135: SCHOOL installment plan configurator (PA Act 1)
    {
      label: 'Installment Plan',
      icon: GraduationCap,
      path: '/collector/installment-plan',
      roles: ['CollectorAdmin', 'CollectorStaff'],
    },
    {
      label: 'Assessment Changes',
      icon: TrendingUp,
      path: '/collector/assessment-changes',
      roles: ['CollectorAdmin', 'CollectorStaff'],
    },
    // Deploy 7: Bill Templates hidden from sidebar. Actual bill layout is Python-
    // code-defined in pa_tax_bill_templates.py; the DB template only carries
    // discount/face/penalty period configs which are now overridden by the
    // Billing Calendar editor (PC130 / PC59+PC60). Editing this "template" gave
    // Millie no visible change on bills, only confusion. Data-driven bill
    // templates are Q1 2027 work.
    // {
    //   label: 'Bill Templates',
    //   icon: LayoutTemplate,
    //   path: '/collector/billing/templates',
    //   roles: ['CollectorAdmin', 'CollectorStaff'],
    // },
    {
      label: 'Collector Settings',
      icon: Settings,
      path: '/collector/settings',
      roles: ['CollectorAdmin', 'CollectorStaff'],  // PC93: removed SystemAdmin; U2b: staff can open it (saves follow their access level)
    },
    // Deploy 7: First-Time Setup hidden from sidebar. Deploy 8 converts this to
    // a first-login modal that dismisses once setup completes. Path stays live
    // so any redirect targeting it still works.
    // {
    //   label: 'First-Time Setup',
    //   icon: Sparkles,
    //   path: '/collector/first-time-setup',
    //   roles: ['CollectorAdmin'],
    // },
    // R10e36.53.3-F393: /certifications nav link removed — legacy page deleted, redirects to /certificate-admin
    {
      label: 'Report Center',
      icon: BarChart3,
      path: '/reports',
      // PC93: removed SystemAdmin (all 10 reports are collector-scoped by design).
      // Platform-wide admin analytics belong on Platform Overview.
      roles: ['CollectorAdmin', 'CollectorStaff'],
    },
    {
      label: 'Professional Portal',
      icon: Building,
      path: '/professional',
      roles: ['ProfessionalUser'],
    },
    // Deploy 7: renamed "Certificate Admin" -> "Tax Certificate Operations"
    // for clarity — matches what collectors actually call this workflow.
    {
      label: 'Tax Certificate Operations',
      icon: FileCheck,
      path: '/certificate-admin',
      roles: ['CollectorAdmin', 'CollectorStaff'],
    },
    // R10e36.53.2-F382: Add Certificate Pricing nav item for SystemAdmin
    {
      label: 'Certificate Pricing',
      icon: DollarSign,
      path: '/admin/certificate-pricing',
      roles: ['SystemAdmin'],
    },
    // R9 (2026-09-26): SystemAdmin certificate-issuance analytics
    {
      label: 'Certificate Analytics',
      icon: TrendingUp,
      path: '/admin/certificate-analytics',
      roles: ['SystemAdmin'],
    },
    {
      // R10e36.65-F544: Single consolidated nav item for data import/migration
      label: 'Data Migration',
      icon: FolderSync,
      path: '/migrations',
      roles: ['CollectorAdmin'],  // PC93: removed SystemAdmin
    },
    // PC93: Invite User consolidated into "User Management" sidebar item (opens User Mgmt with Invite tab).
    {
      label: 'Jurisdiction Roster',
      icon: Building,
      path: '/admin/roster',
      roles: ['SystemAdmin'],
    },
    {
      label: 'Onboard Collector',
      icon: Users,
      path: '/admin/onboarding',
      roles: ['SystemAdmin'],
    },
    {
      label: 'Jurisdiction Transfers',
      icon: FolderSync,
      path: '/admin/transfers',
      roles: ['SystemAdmin'],
    },
    {
      label: 'Collector Intakes',  // A20
      icon: ClipboardCheck,
      path: '/admin/intakes',
      roles: ['SystemAdmin'],
    },
    {
      label: 'Subscription Billing',  // C3
      icon: Receipt,
      path: '/admin/subscriptions',
      roles: ['SystemAdmin'],
    },
    {
      label: 'Collector Emails',  // C4 click-to-send
      icon: Mail,
      path: '/admin/collector-emails',
      roles: ['SystemAdmin'],
    },
    // R10e35b-032: Platform Analytics hidden until real data implementation
    // {
    //   label: 'Platform Analytics',
    //   icon: BarChart3,
    //   path: '/admin/analytics',
    //   roles: ['SystemAdmin'],
    // },
    {
      label: 'Audit Logs',
      icon: Shield,
      path: '/audit-logs',
      roles: ['SystemAdmin', 'CollectorAdmin'],
    },
    {
      label: 'Settings',
      icon: Settings,
      path: '/settings',
      roles: ['CollectorAdmin', 'CollectorStaff', 'MunicipalViewer', 'TitleCompanyUser', 'SystemAdmin'],
    },
  ];

  // R10e36.71.1-F778: Filter menu items by role, then additionally hide desktop-only routes on mobile
  const filteredMenuItems = menuItems
    .filter((item) => !item.roles || item.roles.includes(user?.user_role || user?.role))
    .filter((item) => !isMobile || !MOBILE_HIDDEN_ROUTES.some(r => item.path.startsWith(r)));

  // PC131 (Deploy 7 Batch 3): Two-level nav — assign each item a group based
  // on its path, then render items under group headers. Groups + order below
  // mirror the xlsx audit's "Consolidation Plan". Keeping the mapping
  // out-of-line so a future contributor can add a group column to the item
  // literal instead of editing this table when they add a route.
  const GROUP_BY_PATH = {
    '/dashboard': 'Daily Operations',
    '/parcels': 'Daily Operations',
    '/payments': 'Daily Operations',
    '/operations': 'Daily Operations',
    '/collector/bills': 'Daily Operations',
    '/certificate-admin': 'Tax Certificates',
    '/collector/mail-queue': 'Tax Certificates',
    '/admin/certificate-pricing': 'Tax Certificates',
    '/admin/certificate-analytics': 'Tax Certificates',  // R9
    '/collector/millage-rates': 'Configuration',
    '/collector/billing-calendar': 'Configuration',  // PC132: standalone dual-cycle calendar editor
    '/collector/installment-plan': 'Configuration',  // PC135: SCHOOL installment plan configurator
    '/collector/assessment-changes': 'Configuration',
    '/collector/charges': 'Configuration',
    '/collector/settings': 'Configuration',
    '/settings': 'Configuration',
    '/migrations': 'Data Management',
    '/historical-import': 'Data Management',
    '/trust-account': 'Compliance & Reports',
    '/audit-logs': 'Compliance & Reports',
    '/reports': 'Compliance & Reports',
    '/professional': 'Portals',
    '/admin/platform-overview': 'Platform Admin',
    '/admin/users': 'Platform Admin',
    '/admin/roster': 'Platform Admin',
    '/admin/onboarding': 'Platform Admin',
    '/admin/transfers': 'Platform Admin',
    '/admin/subscriptions': 'Platform Admin',  // C3
    '/admin/collector-emails': 'Platform Admin',  // C4
    '/admin/intakes': 'Platform Admin',  // A20
  };
  const GROUP_ORDER = [
    'Daily Operations',
    'Tax Certificates',
    'Configuration',
    'Data Management',
    'Compliance & Reports',
    'Portals',
    'Platform Admin',
    'Other',
  ];
  const groupedItems = GROUP_ORDER
    .map((g) => ({
      group: g,
      items: filteredMenuItems.filter((it) => (GROUP_BY_PATH[it.path] || 'Other') === g),
    }))
    .filter((g) => g.items.length > 0);

  return (
    <div className="min-h-screen bg-gradient-to-b from-slate-50 to-white">
      {/* PC137: Impersonation banner — only renders if the session is impersonated */}
      <ImpersonationBanner />
      <header className="bg-white/70 backdrop-blur-xl border-b border-border/20 sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-4 lg:px-12 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-3">
              {/* R10e36.68-F823: White-label header uses collector office info from auth context.
                  PC135d: BrandLogo now serves the RECT platform logo from
                  /branding/rect-logo.png for collector-variant headers.
                  Larger sizing (h-12) so the logo has room; wider container
                  so the aspect ratio isn't squashed. */}
              <BrandLogo variant="collector" collectorId={user?.collector_office_id} className="h-12 w-auto" />
              <div>
                <h1 className="text-lg font-bold font-heading text-slate-900">
                  {(() => {
                    const collectorOfficeName = user?.collector_office_name || user?.jurisdiction_name || BRAND.collectorAppNeutralName;
                    return collectorOfficeName.toLowerCase().includes('tax collector')
                      ? collectorOfficeName
                      : `${collectorOfficeName} Tax Collector`;
                  })()}
                </h1>
                {/* PC135d: "PA Act 48 Collection System" tagline removed —
                    the logo + collector office name already frame what this
                    surface is. */}
              </div>
            </div>

            <div className="flex items-center space-x-4">
              {/* PC133 (Deploy 7 Batch 3 hotfix): Consolidated the two separate
                  notification icons (NotificationBell for PDF job completions +
                  NotificationCenter for timeline/deadlines) down to a single
                  bell. NotificationCenter is the richer surface and the one
                  collectors actually check for deadlines. PDF job status is
                  still surfaced live via the Bill Runs page state machine
                  (PC90) so removing the second bell doesn't lose info.
                  Full merge (bringing PDF notifications INTO
                  NotificationCenter) is queued as a follow-up. */}
              <NotificationCenter />
              <div className="text-right hidden sm:block">
                <p className="text-sm font-medium text-slate-900">
                  {user?.first_name} {user?.last_name}
                </p>
                <p className="text-xs text-slate-500">{user?.role}</p>
              </div>
              <Button
                variant="outline"
                size="sm"
                onClick={handleLogout}
                data-testid="logout-button"
                className="flex items-center"
              >
                <LogOut className="w-4 h-4 mr-2" />
                Logout
              </Button>
            </div>
          </div>
        </div>
      </header>

      <div className="max-w-7xl mx-auto px-4 lg:px-12 py-8">
        <div className="flex flex-col lg:flex-row gap-8">
          {/* Mobile Menu Toggle */}
          <div className="lg:hidden mb-4">
            <Button
              variant="outline"
              onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
              className="w-full flex items-center justify-center gap-2"
              data-testid="mobile-menu-toggle"
            >
              {mobileMenuOpen ? <X className="w-4 h-4" /> : <Menu className="w-4 h-4" />}
              {mobileMenuOpen ? 'Close Menu' : 'Open Menu'}
            </Button>
          </div>
          
          <aside className={`w-full lg:w-64 shrink-0 ${mobileMenuOpen ? 'block' : 'hidden lg:block'}`}>
            <nav className="bg-card border border-border rounded-lg p-2 sticky top-24" data-testid="sidebar-navigation">
              {/* PC131 (Deploy 7 Batch 3): two-level grouped nav. Each group
                  renders as a small uppercase header followed by its items.
                  Single top-level items (Portals, Settings) still render
                  correctly with a one-item group. */}
              {groupedItems.map(({ group, items }) => (
                <div key={group} className="mb-3 last:mb-0">
                  <div
                    className="px-3 pt-2 pb-1 text-[10px] font-semibold uppercase tracking-wider text-slate-400"
                    data-testid={`nav-group-${group.toLowerCase().replace(/\s+/g, '-')}`}
                  >
                    {group}
                  </div>
                  <ul className="space-y-1">
                    {items.map((item) => {
                      const Icon = item.icon;
                      const isActive = window.location.pathname === item.path;
                      return (
                        <li key={item.path}>
                          <button
                            onClick={() => {
                              navigate(item.path);
                              setMobileMenuOpen(false);
                            }}
                            data-testid={`nav-${item.label.toLowerCase().replace(/\s+/g, '-')}`}
                            className={`w-full flex items-center space-x-3 px-3 py-2 rounded-md text-sm font-medium transition-colors ${
                              isActive
                                ? 'bg-primary text-white'
                                : 'text-slate-700 hover:bg-slate-100'
                            }`}
                          >
                            <Icon className="w-4 h-4" />
                            <span>{item.label}</span>
                          </button>
                        </li>
                      );
                    })}
                  </ul>
                </div>
              ))}
            </nav>
          </aside>

          <main className="flex-1 min-w-0">
            {/* Show bootstrap status in development */}
            {process.env.NODE_ENV === 'development' && <BootstrapStatus />}
            {/* R10e36.59-F449: breadcrumb slot for back-nav */}
            {breadcrumb && (
              <div className="mb-4 px-6 pt-4">
                {breadcrumb}
              </div>
            )}
            {children}
          </main>
        </div>
      </div>
      {/* R10e36.68-F823: Powered-by footer for collector app */}
      <footer className="text-center py-4 text-xs text-slate-400">
        Powered by {BRAND.legalEntity}
      </footer>
    </div>
  );
};

export default DashboardLayout;
